import React from 'react'
import { AlertTriangle, Monitor, Clock, XCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useWebSocketContext } from '@/context/WebSocketContext'
import { Badge } from '@/components/ui/badge'
import api from '@/lib/axios'

interface DeviceLogNotification {
  _id: string
  type: string
  title: string
  message: string
  read: boolean
  data?: {
    logId?: string
    deviceToken?: string
    deviceName?: string
    category?: string
    level?: string
    errorData?: any
  }
  createdAt: string
}

interface DeviceLogNotificationsProps {
  isOpen: boolean
  onClose: () => void
}

export const DeviceLogNotifications: React.FC<DeviceLogNotificationsProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const { addMessageHandler, removeMessageHandler } = useWebSocketContext()
  const [notifications, setNotifications] = React.useState<DeviceLogNotification[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const debounceTimerRef = React.useRef<NodeJS.Timeout>()

  // Ses çal
  const playNotificationSound = React.useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()

      const playTone = (frequency: number, startTime: number, duration: number) => {
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.value = frequency
        oscillator.type = 'sine'

        gainNode.gain.setValueAtTime(0.5, startTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration)

        oscillator.start(startTime)
        oscillator.stop(startTime + duration)
      }

      const now = audioContext.currentTime
      playTone(600, now, 0.1)        // İlk ton (düşük)
      playTone(400, now + 0.1, 0.1)  // İkinci ton (daha düşük - alarm tarzı)
      playTone(600, now + 0.2, 0.1)  // Üçüncü ton
    } catch (error) {
      console.error('Ses çalınamadı:', error)
    }
  }, [])

  // Device log bildirimlerini getir
  const fetchNotifications = React.useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await api.get('/notifications')
      // Sadece deviceLog tipindeki okunmamış bildirimleri filtrele
      const deviceLogNotifications = (response.data || []).filter(
        (n: DeviceLogNotification) => n.type === 'deviceLog' && !n.read
      )
      setNotifications(deviceLogNotifications)
    } catch (error) {
      console.error('Device log bildirimleri getirilirken hata:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Debounced fetch
  const debouncedFetchNotifications = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchNotifications()
    }, 300)
  }, [fetchNotifications])

  // Component mount olduğunda ve dropdown açıldığında bildirimleri getir
  React.useEffect(() => {
    if (isOpen) {
      fetchNotifications()
    }
  }, [isOpen, fetchNotifications])

  // İlk yüklemede de getir
  React.useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // WebSocket ile gerçek zamanlı güncellemeler
  React.useEffect(() => {
    const handleMessage = (message: any) => {
      if (message.type === 'device_log_error' || message.type === 'notification_new') {
        playNotificationSound()
        debouncedFetchNotifications()
      }
    }

    addMessageHandler(handleMessage)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      removeMessageHandler(handleMessage)
    }
  }, [addMessageHandler, removeMessageHandler, debouncedFetchNotifications, playNotificationSound])

  // Bildirime tıklandığında
  const handleNotificationClick = async (notification: DeviceLogNotification) => {
    // Bildirimi okundu olarak işaretle
    try {
      await api.post('/notifications/mark-all-read')
    } catch (error) {
      console.error('Bildirim okundu işaretlenirken hata:', error)
    }

    // Log sayfasına yönlendir - device token ve logId ile filtrele
    const params = new URLSearchParams()
    if (notification.data?.deviceToken) {
      params.set('deviceToken', notification.data.deviceToken)
    }
    if (notification.data?.category) {
      params.set('category', notification.data.category)
    }
    if (notification.data?.logId) {
      params.set('logId', notification.data.logId)
    }

    navigate(`/device-logs?${params.toString()}`)
    onClose()
  }

  // Tüm logları göster
  const handleViewAll = () => {
    navigate('/device-logs')
    onClose()
  }

  // Zaman formatlama
  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Az önce'
    if (diffMins < 60) return `${diffMins} dk önce`
    if (diffHours < 24) return `${diffHours} saat önce`
    return `${diffDays} gün önce`
  }

  // Kategori ikonu
  const CategoryIcon = ({ category }: { category?: string }) => {
    switch (category) {
      case 'PLAYBACK':
        return <XCircle className="w-4 h-4 text-red-500" />
      case 'ANNOUNCEMENT':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />
      default:
        return <AlertTriangle className="w-4 h-4 text-red-500" />
    }
  }

  if (!isOpen) return null

  return (
    <div className="absolute right-0 mt-2 w-96 bg-card rounded-lg shadow-lg border border-border max-h-[500px] flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-base flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            Cihaz Hataları
          </h3>
          {notifications.length > 0 && (
            <Badge className="bg-red-500 text-white hover:bg-red-600">
              {notifications.length} yeni
            </Badge>
          )}
        </div>
      </div>

      {/* Notification List */}
      <div className="overflow-y-auto flex-1">
        {isLoading ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            Yükleniyor...
          </div>
        ) : notifications.length === 0 ? (
          <div className="p-8 text-center">
            <Monitor className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Yeni cihaz hatası yok</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {notifications.map((notification) => (
              <div
                key={notification._id}
                onClick={() => handleNotificationClick(notification)}
                className="p-3 hover:bg-muted cursor-pointer transition-colors"
              >
                <div className="flex items-start gap-3">
                  <CategoryIcon category={notification.data?.category} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      <span className="text-xs font-medium text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
                        {notification.data?.category || 'ERROR'}
                      </span>
                    </div>
                    <p className="font-medium text-sm text-foreground truncate">
                      {notification.title}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                      <Monitor className="w-3 h-3" />
                      <span className="truncate">
                        {notification.data?.deviceName || notification.data?.deviceToken || 'Bilinmeyen cihaz'}
                      </span>
                      <span>•</span>
                      <Clock className="w-3 h-3" />
                      <span>{formatTime(notification.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {notifications.length > 0 && (
        <div className="p-3 border-t border-border">
          <button
            onClick={handleViewAll}
            className="w-full text-center text-sm font-medium text-primary hover:text-primary/80 transition-colors py-2"
          >
            Tüm Cihaz Loglarını Görüntüle
          </button>
        </div>
      )}
    </div>
  )
}

// Hook to get unread device log notification count for badge
export const useUnreadDeviceLogCount = () => {
  const { addMessageHandler, removeMessageHandler } = useWebSocketContext()
  const [unreadCount, setUnreadCount] = React.useState(0)
  const debounceTimerRef = React.useRef<NodeJS.Timeout>()

  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const response = await api.get('/notifications')
      const deviceLogNotifications = (response.data || []).filter(
        (n: DeviceLogNotification) => n.type === 'deviceLog' && !n.read
      )
      setUnreadCount(deviceLogNotifications.length)
    } catch (error) {
      console.error('Okunmamış device log sayısı alınamadı:', error)
    }
  }, [])

  // Debounced fetch
  const debouncedFetchUnreadCount = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
    debounceTimerRef.current = setTimeout(() => {
      fetchUnreadCount()
    }, 300)
  }, [fetchUnreadCount])

  React.useEffect(() => {
    fetchUnreadCount()
    // Her 30 saniyede bir kontrol et
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  // WebSocket ile gerçek zamanlı güncellemeler
  React.useEffect(() => {
    const handleMessage = (message: any) => {
      if (message.type === 'device_log_error' || message.type === 'notification_new') {
        debouncedFetchUnreadCount()
      }
    }

    addMessageHandler(handleMessage)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      removeMessageHandler(handleMessage)
    }
  }, [addMessageHandler, removeMessageHandler, debouncedFetchUnreadCount])

  return unreadCount
}
